// trade-planet-admin/src/stores/rareItems.js

import { defineStore } from "pinia";
import { ref } from "vue";
import axios from "axios";

// The API's validate middleware returns a generic "error" message plus a
// per-field "details" array (e.g. [{ field: "CreatedDate", message: "..." }]) —
// surface the actual field/reason instead of just "Validation failed.".
const describeErrorPayload = (data, fallback) => {
  const base = data?.error || fallback;
  const details = Array.isArray(data?.details) && data.details.length
    ? " " + data.details.map((d) => `${d.field}: ${d.message}`).join("; ")
    : "";
  return base + details;
};

const describeApiError = (error, fallback) => describeErrorPayload(error.response?.data, error.message || fallback);

export const useRareItemsStore = defineStore("rareItems", () => {
  const API_BASE_URL = process.env.VUE_APP_API_BASE_URL;

  const rareItems = ref([]);
  const searchedItems = ref([]);
  const selectedItem = ref({});
  const totalRareItems = ref(0);
  const totalAllRareItems = ref(0);
  const totalRap = ref(0);
  const totalOverallRAP = ref(0);
  const totalValue = ref(0);
  const totalOverallValue = ref(0);
  const itemEditLogs = ref([]);
  const rareItemEditLogs = ref([]);
  const totalRareItemLogs = ref(0);

  async function fetchAllRareItems(perPage = 200) {
    try {
      const all = [];
      let start = 0;
      let keepFetching = true;
  
      while (keepFetching) {
        const response = await axios.get(`${API_BASE_URL}/v2/unique/items`, {
          params: { start, limit: perPage, sortBy: "ID", order: "desc" },
        });
  
        const batch = Array.isArray(response.data) ? response.data : [];
  
        if (!batch.length) {
          keepFetching = false;
          break;
        }
  
        all.push(...batch);
  
        if (batch.length < perPage) {
          keepFetching = false;
          break;
        }
  
        start += perPage;
      }
  
      rareItems.value = all.map((item) => ({
        id: item.ID,
        itemId: item.ItemID,
        name: item.ItemName,
        img: item.ImageLink,
        rap: item.RAP,
        value: item.Value,
      }));
    } catch (error) {
      console.error("Error fetching all rare items:", error);
    }
  }
  
  async function fetchRareItems(page = 0, perPage = 30, sortBy = "ID", order = "DESC") {
    try {
      const params = {
        start: page * perPage,
        limit: perPage,
        sortBy,
        order: String(order).toLowerCase() === "asc" ? "asc" : "desc",
      };
      const response = await axios.get(`${API_BASE_URL}/v2/unique/items`, { params });

      if (!response.data || !Array.isArray(response.data)) return [];

      rareItems.value = response.data.map((item) => ({
        id: item.ID,
        itemId: item.ItemID,
        name: item.ItemName,
        type: item.Type,
        img: item.ImageLink,
        rap: item.RAP,
        value: item.Value,
        totalOwners: item.TotalOwners,
        isCrateItem: item.IsCrateItem,
        isProjected: item.IsProjected,
        isUgc: item.IsUgc,
        isAstro: item.IsAstro,
      }));

      return rareItems.value;
    } catch (error) {
      console.error("Error fetching rare items:", error);
      return [];
    }
  }

  async function fetchProjectedRareItems(page = 0, perPage = 30, sortBy = "ID", order = "DESC") {
    try {
      const params = {
        start: page * perPage,
        limit: perPage,
        sortBy,
        order: String(order).toLowerCase() === "asc" ? "asc" : "desc",
      };      
      const response = await axios.get(`${API_BASE_URL}/v2/unique/items/projected`, { params });

      if (!response.data || !Array.isArray(response.data)) return [];

      rareItems.value = response.data.map((item) => ({
        id: item.ID,
        itemId: item.ItemID,
        name: item.ItemName,
        type: item.Type,
        img: item.ImageLink,
        rap: item.RAP,
        value: item.Value,
        totalOwners: item.TotalOwners,
        isCrateItem: item.IsCrateItem,
        isProjected: item.IsProjected,
        isUgc: item.IsUgc,
        isAstro: item.IsAstro,
      }));

      return rareItems.value;
    } catch (error) {
      console.error("Error fetching projected rare items:", error);
      return [];
    }
  }

  async function searchRareItems(query) {
    if (!query) {
      searchedItems.value = [];
      return;
    }

    try {
      const response = await axios.get(`${API_BASE_URL}/v2/unique/items/search`, {
        params: { query, limit: 100 },
      });

      searchedItems.value = response.data.map((item) => ({
        id: item.ID,
        itemId: item.ItemID,
        name: item.ItemName,
        img: item.ImageLink,
        rap: item.RAP,
        value: item.Value,
      }));
    } catch (error) {
      console.error("Error searching rare items:", error);
    }
  }

  async function fetchItemById(itemId) {
    if (!itemId) return;
    selectedItem.value = {};

    try {
      const response = await axios.get(`${API_BASE_URL}/v2/unique/item/${itemId}`);
      const data = response.data;

      if (data && data.ItemID) {
        selectedItem.value = {
          itemId: data.ItemID,
          name: data.ItemName,
          acronym: data.Acronym || "N/A",
          type: data.Type ?? 0,
          imageLink: data.ImageLink || "",
          originalPriceBits: data.OriginalPriceBits ?? 0,
          originalPriceCredits: data.OriginalPriceCredits ?? 0,
          totalOwners: data.TotalOwners ?? 0,
          createdDate: data.CreatedDate || "Unknown",
          discoveredDate: data.DiscoveredDate || "Unknown",
          isUgc: Boolean(data.IsUgc),
          isAstro: Boolean(data.IsAstro),
          isDeleted: Boolean(data.IsDeleted),
          isProjected: Boolean(data.IsProjected),
          isCrateItem: data.IsCrateItem === 1,
          rap: data.RAP ?? 0,
          value: data.Value ?? 0,
          rarityType: data.RarityType ?? 0,
          crateRarityType: data.CrateRarityType ?? 0,
          crateSourceId: data.CrateSourceID ?? null,
          crateDrops: Array.isArray(data.CrateDrops)
            ? data.CrateDrops.map((drop) => ({
                itemId: drop.ItemID,
                name: drop.ItemName,
                acronym: drop.Acronym || "N/A",
                imageLink: drop.ImageLink || "",
                crateRarityType: drop.CrateRarityType ?? 0,
              }))
            : [],
        };
      }
    } catch (error) {
      console.error("Error fetching item details:", error);
    }
  }

  async function updateItem(itemId, updatedFields, userPermissions = []) {
    try {
      const has = (p) => userPermissions.includes(p);

      const payloadItem = {};
      const payloadRap = {};
      const payloadValue = {};

      /*
      for (const [k, v] of Object.entries(updatedFields || {})) {
        if (k === "RAP") payloadRap[k] = v;
        else if (k === "Value") payloadValue[k] = v;
        else if (k === "IsProjected") payloadValue[k] = v;
        else payloadItem[k] = v;
      }
      */
      for (const [k, v] of Object.entries(updatedFields || {})) {
        if (k === "RAP") payloadRap[k] = v;
        else if (k === "Value") payloadValue[k] = v;
        else if (k === "IsProjected") {
          if (has("edit_items")) payloadItem[k] = v;
          else payloadValue[k] = v;
        } else {
          payloadItem[k] = v;
        }
      }
  
      const results = [];

      if (Object.keys(payloadItem).length > 0) {
        if (!has("edit_items")) {
          return { success: false, message: "You do not have permission to edit item details." };
        }
  
        /*
        const r = await axios.post(
          `${API_BASE_URL}/v2/unique/item/${itemId}/update`,
          { updatedFields: payloadItem },
          { headers: getAuthHeaders() }
        );

        results.push(r.data);
        */

        const r = await axios
          .post(`${API_BASE_URL}/v2/unique/item/${itemId}/update`, { updatedFields: payloadItem })
          .then(res => res.data)
          .catch(err => err.response?.data || { success: false, message: err.message });

        results.push(r);
      }

      if (Object.keys(payloadRap).length > 0) {
        if (!has("edit_rap")) {
          return { success: false, message: "You do not have permission to edit RAP." };
        }
  
        const r = await axios.post(
          `${API_BASE_URL}/v2/unique/item/${itemId}/update-rap`,
          { updatedFields: payloadRap }
        );
        results.push(r.data);
      }

      if (Object.keys(payloadValue).length > 0) {
        if (!has("edit_value")) {
          return { success: false, message: "You do not have permission to edit Value." };
        }
  
        const r = await axios.post(
          `${API_BASE_URL}/v2/unique/item/${itemId}/update-value`,
          { updatedFields: payloadValue }
        );
        results.push(r.data);
      }

      if (results.length === 0) {
        return { success: false, message: "No changes detected." };
      }

      const failed = results.find((x) => x?.success !== true);
      if (failed) {
        return { success: false, message: describeErrorPayload(failed, failed?.message || "Update failed.") };
      }

      return { success: true, message: "Item updated successfully." };
    } catch (error) {
      return { success: false, message: describeApiError(error, "Error updating item.") };
    }
  }

  async function createRareItem(itemData) {
    try {
      const response = await axios.post(`${API_BASE_URL}/v2/unique/item/add`, itemData);

      return response.data?.success
        ? { success: true, message: response.data.message || "Item created successfully." }
        : { success: false, message: response.data.error || "Failed to create item." };
    } catch (error) {
      return { success: false, message: describeApiError(error, "Error creating item.") };
    }
  }

  async function fetchRareItemsCount() {
    try {
      const response = await axios.get(`${API_BASE_URL}/v2/unique/items/count`);
      totalRareItems.value = Number(response.data?.Rares) || 0;
    } catch (error) {
      console.error("Error fetching rare items count:", error);
      totalRareItems.value = 0;
    }
  }

  async function fetchAllRareItemsCount() {
    try {
      const response = await axios.get(`${API_BASE_URL}/v2/unique/items/all/count`);
      totalAllRareItems.value = Number(response.data?.AllRares) || 0;
    } catch (error) {
      console.error("Error fetching all rare items count:", error);
      totalAllRareItems.value = 0;
    }
  }

  async function fetchTotalRAPValueStats() {
    try {
      const response = await axios.get(`${API_BASE_URL}/v2/unique/items/total-stats`);

      totalRap.value = Number(response.data?.RAPTotal) || 0;
      totalOverallRAP.value = Number(response.data?.TotalOverallRAP) || 0;
      totalValue.value = Number(response.data?.ValueTotal) || 0;
      totalOverallValue.value = Number(response.data?.TotalOverallValue) || 0;
      
    } catch (error) {
      console.error("Error fetching RAP/Value totals:", error);
      totalRap.value = 0;
      totalOverallRAP.value = 0;
      totalValue.value = 0;
      totalOverallValue.value = 0;
    }
  }

  async function fetchRareItemEditLogs(start = 0, limit = 50, adminId = null, itemId = null) {
    try {
      const params = { start, limit };
      if (adminId != null && adminId !== "") params.adminId = adminId;
      if (itemId != null && itemId !== "") params.itemId = itemId;
  
      const response = await axios.get(`${API_BASE_URL}/v2/unique/items/edit-logs`, { params });
  
      // model returns an array (not {total,data}) for this endpoint right now
      rareItemEditLogs.value = Array.isArray(response.data) ? response.data : [];
      return rareItemEditLogs.value;
    } catch (error) {
      console.error("Error fetching rare item edit logs:", error);
      rareItemEditLogs.value = [];
      return [];
    }
  }
  
  async function fetchRareItemValueEditLogs(start = 0, limit = 50, adminId = null, itemId = null) {
    try {
      const params = { start, limit };
      if (adminId != null && adminId !== "") params.adminId = adminId;
      if (itemId != null && itemId !== "") params.itemId = itemId;
  
      const response = await axios.get(`${API_BASE_URL}/v2/unique/items/value-edit-logs`, { params });
  
      itemEditLogs.value = Array.isArray(response.data?.data) ? response.data.data : (Array.isArray(response.data) ? response.data : []);
      totalRareItemLogs.value = Number(response.data?.total || 0) || 0;
  
      return itemEditLogs.value;
    } catch (error) {
      console.error("Error fetching value edit logs:", error);
      itemEditLogs.value = [];
      totalRareItemLogs.value = 0;
      return [];
    }
  }
  
  async function fetchEditLogsCount() {
    try {
      const response = await axios.get(`${API_BASE_URL}/v2/unique/items/edit-logs/count`);
      totalRareItemLogs.value = Number(response.data?.totalEdits || 0) || 0;
      return totalRareItemLogs.value;
    } catch (error) {
      console.error("Error fetching edit logs count:", error);
      totalRareItemLogs.value = 0;
      return 0;
    }
  }  
  return {
    rareItems,
    searchedItems,
    selectedItem,
    totalRareItems,
    totalAllRareItems,
    totalRap,
    totalOverallRAP,
    totalValue,
    totalOverallValue,
    itemEditLogs,
    rareItemEditLogs,
    totalRareItemLogs,
    fetchAllRareItems,
    fetchRareItems,
    fetchProjectedRareItems,
    searchRareItems,
    fetchItemById,
    updateItem,
    createRareItem,
    fetchRareItemsCount,
    fetchAllRareItemsCount,
    fetchTotalRAPValueStats,
    fetchRareItemEditLogs,
    fetchRareItemValueEditLogs,
    fetchEditLogsCount,
  };
});